"use client";

import logger from "@/utils/logger";

import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    ReactNode,
} from "react";
import GlobalNotificationBanner, {
    BannerNotification,
} from "@/components/notifications/GlobalNotificationBanner";

interface BannerContextType {
    banners: BannerNotification[];
    addBanner: (banner: Omit<BannerNotification, "id" | "createdAt">) => void;
    removeBanner: (id: string) => void;
}

const GlobalBannerContext = createContext<BannerContextType | undefined>(
    undefined
);

export const useGlobalBanner = () => {
    const context = useContext(GlobalBannerContext);
    if (!context) {
        throw new Error(
            "useGlobalBanner must be used within GlobalBannerProvider"
        );
    }
    return context;
};

interface GlobalBannerProviderProps {
    children: ReactNode;
}

export const GlobalBannerProvider: React.FC<GlobalBannerProviderProps> = ({
    children,
}) => {
    const [banners, setBanners] = useState<BannerNotification[]>([]);

    const addBanner = useCallback(
        (banner: Omit<BannerNotification, "id" | "createdAt">) => {
            setBanners((prev) => {
                // Check if banner for this tripId already exists
                const existingBanner = prev.find(
                    (b) => b.tripId === banner.tripId
                );

                if (existingBanner) {
                    logger.info(
                        "⏭️ [GlobalBannerProvider] Banner for trip already exists, skipping:",
                        { tripId: banner.tripId, existingId: existingBanner.id }
                    );
                    return prev; // Don't add duplicate
                }

                const id = `banner-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                const newBanner: BannerNotification = {
                    ...banner,
                    id,
                    createdAt: new Date(),
                };

                logger.info(
                    "🚨 [GlobalBannerProvider] Adding banner:",
                    newBanner
                );

                // Auto-dismiss banner after 15 seconds
                setTimeout(() => {
                    logger.info(
                        "⏱️ [GlobalBannerProvider] Auto-dismissing banner after 15s:",
                        id
                    );
                    setBanners((current) => current.filter((b) => b.id !== id));
                }, 15000);

                // Limit to maximum 3 banners to prevent overflow
                const updated = [...prev, newBanner];
                if (updated.length > 3) {
                    logger.info(
                        "⚠️ [GlobalBannerProvider] Max banners reached, removing oldest"
                    );
                    return updated.slice(-3);
                }
                return updated;
            });
        },
        []
    );

    const removeBanner = useCallback((id: string) => {
        logger.info("🗑️ [GlobalBannerProvider] Removing banner:", id);
        setBanners((prev) => prev.filter((banner) => banner.id !== id));
    }, []);

    return (
        <GlobalBannerContext.Provider
            value={{ banners, addBanner, removeBanner }}
        >
            {children}
            <GlobalNotificationBanner
                notifications={banners}
                onDismiss={removeBanner}
            />
        </GlobalBannerContext.Provider>
    );
};
